/**
 * Layout for /admin/$slug/* — brand chrome wrapping the active child route.
 *
 * The admin nav lives here (not on individual sub-pages) so every admin
 * page shows the same navigation strip. AppHeader's `admin` variant
 * renders the nav inline at desktop widths and collapses into a
 * hamburger panel on mobile. Tenant identity is conveyed by the
 * top-right TenantSwitcherMenu inside the header.
 *
 * Role-aware nav: regular staff see only "My day" + "Approvals" — the
 * full operator surface is hidden to keep them anchored to their own
 * day. Owner + admin see the full nav.
 */
import { Link, Outlet, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@bookaburra/ui';
import { AlertTriangle } from 'lucide-react';
import { AppHeader, type AdminNavItem } from '../components/AppHeader';
import { fetchMyMemberships } from '../booking-flow/tenant-api';
import { fetchBookingTenant } from '../booking-flow/api';

export const Route = createFileRoute('/admin/$slug')({
  component: AdminSlugLayout,
});

function AdminSlugLayout() {
  const { slug } = Route.useParams();
  const meQ = useQuery({
    queryKey: ['me-memberships'],
    queryFn: fetchMyMemberships,
    staleTime: 60_000,
  });
  const currentRole = meQ.data?.memberships.find((m) => m.tenant.slug === slug)?.role;
  const isStaff = currentRole === 'staff';

  // Surface a top-of-page banner when the tenant hasn't published
  // business hours yet. The public booking page hides itself,
  // server-side POST /book refuses, and voice + chat APIs refuse
  // the handoff — but the admin needs to know *why* their booking
  // page is dark, with a one-click route to the fix. Hidden from
  // staff (they can't edit Settings anyway).
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  const hoursMissing =
    !tenantQ.isLoading && !tenantQ.data?.tenant.businessHours;
  const showHoursBanner = hoursMissing && !isStaff;

  const fullNav: AdminNavItem[] = [
    {
      kind: 'menu',
      label: 'Calendar',
      items: [
        { to: '/admin/$slug', label: 'Bookings', params: { slug } },
        { to: '/admin/$slug/my-day', label: 'My day', params: { slug } },
        { to: '/admin/$slug/approvals', label: 'Approvals', params: { slug } },
        { to: '/admin/$slug/waitlist', label: 'Waiting list', params: { slug } },
        { to: '/admin/$slug/closed-days', label: 'Closed days', params: { slug } },
        { to: '/admin/$slug/reshuffle', label: 'Reshuffle', params: { slug } },
        { to: '/admin/$slug/roster', label: 'Roster', params: { slug } },
      ],
    },
    {
      kind: 'menu',
      label: 'Services',
      items: [
        { to: '/admin/$slug/services', label: 'Services', params: { slug } },
        { to: '/admin/$slug/translations', label: 'Translations', params: { slug } },
      ],
    },
    {
      kind: 'menu',
      label: 'Clients',
      items: [
        { to: '/admin/$slug/clients', label: 'All clients', params: { slug } },
        { to: '/admin/$slug/lists', label: 'Lists', params: { slug } },
      ],
    },
    {
      kind: 'menu',
      label: 'Team',
      items: [
        { to: '/admin/$slug/members', label: 'Members', params: { slug } },
        { to: '/admin/$slug/skills', label: 'Skills', params: { slug } },
      ],
    },
    { to: '/admin/$slug/resources', label: 'Resources', params: { slug } },
    {
      kind: 'menu',
      label: 'More',
      items: [
        { to: '/admin/$slug/templates', label: 'Templates', params: { slug } },
        { to: '/admin/$slug/settings', label: 'Settings', params: { slug } },
        { to: '/admin/$slug/billing', label: 'Billing', params: { slug } },
      ],
    },
  ];

  const staffNav: AdminNavItem[] = [
    { to: '/admin/$slug/my-day', label: 'My day', params: { slug } },
    { to: '/admin/$slug/my-availability', label: 'My availability', params: { slug } },
    { to: '/admin/$slug/approvals', label: 'Approvals', params: { slug } },
  ];

  const nav = isStaff ? staffNav : fullNav;

  return (
    <div className="min-h-dvh bg-background">
      <AppHeader admin={{ currentSlug: slug, nav }} />
      {showHoursBanner ? (
        <div className="border-b border-amber-500/40 bg-amber-50">
          <div className="mx-auto max-w-6xl px-6 py-4 sm:px-12">
            <Alert className="border-0 bg-transparent p-0 text-amber-900">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle>Your booking page is paused</AlertTitle>
              <AlertDescription>
                You haven&rsquo;t published your business hours yet, so
                the public booking page is showing a &ldquo;bookings
                paused&rdquo; banner instead of taking bookings.{' '}
                <Link
                  to="/admin/$slug/settings"
                  params={{ slug }}
                  className="font-medium underline underline-offset-2"
                >
                  Open Settings → Business hours
                </Link>{' '}
                to fix.
              </AlertDescription>
            </Alert>
          </div>
        </div>
      ) : null}
      <Outlet />
    </div>
  );
}
